import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import queryString from "query-string";
import {
  Container,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Drawer,
  Button,
  Divider,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Loading from "../../components/Loading";
import VolverButton from "../../components/VolverButton";
import withAuthenticationRequired from "../../components/ProtectedRoutes/withAuthenticationRequired";
import { api } from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";

const headCellSx = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#0B0060",
  fontFamily: "Bricolage Grotesque",
};

const ESTADOS_TABS = [
  { value: "pendiente", label: "Pendientes" },
  { value: "aprobada", label: "Aprobadas" },
  { value: "rechazada", label: "Rechazadas" },
];

const ESTADO_STYLE = {
  pendiente: { label: "Pendiente", bg: "#FFEFC2", color: "#7A4E00" },
  aprobada: { label: "Aprobada", bg: "#B6FECB", color: "#216735" },
  rechazada: { label: "Rechazada", bg: "#FFD6D6", color: "#8A1F1F" },
};

// par etiqueta/valor estilo "detalles" (label gris arriba, valor debajo)
const Campo = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={{ color: "#8A8F98", fontSize: "14px", mb: "2px" }}>{label}</Typography>
    <Typography sx={{ color: "#1a1a1a", fontSize: "16px", wordBreak: "break-word" }}>{value || "-"}</Typography>
  </Box>
);

// agrupa las respuestas de info adicional por ticket cuando el item tiene cantidad > 1
const agruparRespuestas = (item) => {
  const respuestas = item.respuestas || [];
  const cantidad = item.cantidad || 1;
  if (respuestas.length === 0) return [];
  if (cantidad > 1 && respuestas.length >= cantidad && respuestas.length % cantidad === 0) {
    const porTicket = respuestas.length / cantidad;
    const grupos = [];
    for (let i = 0; i < cantidad; i++) {
      grupos.push(respuestas.slice(i * porTicket, (i + 1) * porTicket));
    }
    return grupos;
  }
  return [respuestas]; // un solo grupo (todo junto)
};

const SolicitudesTickets = () => {
  const param = useParams();

  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);

  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({ current: 0, pageSize: 10, total: 0 });
  const [activeTab, setActiveTab] = useState("pendiente");
  const [descargando, setDescargando] = useState(false);
  const [menuReporteAnchor, setMenuReporteAnchor] = useState(null);

  const [selected, setSelected] = useState(null); // solicitud abierta en el drawer
  const [rechazando, setRechazando] = useState(false); // modo motivo dentro del drawer
  const [motivo, setMotivo] = useState("");
  const [procesando, setProcesando] = useState(false);

  // menú de acceso directo (tres puntitos) por fila
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuSolicitud, setMenuSolicitud] = useState(null);

  const abrirMenu = (e, solicitud) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuSolicitud(solicitud);
  };
  const cerrarMenu = () => {
    setMenuAnchor(null);
    setMenuSolicitud(null);
  };

  const fetchSolicitudes = useCallback(async () => {
    setLoading(true);
    try {
      const qs = queryString.stringify({
        q: searchTerm.trim(),
        estado: activeTab,
        limit: pagination.pageSize,
        page: pagination.current,
      });
      const res = await api.solicitudes.getPendientes(param.id, qs);
      if (res.status === "success") {
        setSolicitudes(res.data?.data || []);
        setPagination((prev) => ({ ...prev, total: res.data?.totalDataSize || 0 }));
      } else {
        toast.error(res.message || "Error al cargar solicitudes");
      }
    } catch (e) {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.id, searchTerm, activeTab, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchSolicitudes();
  }, [fetchSolicitudes]);

  const onSearch = (value) => {
    setPagination((prev) => ({ ...prev, current: 0 }));
    setSearchTerm(value);
  };

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
    setPagination((prev) => ({ ...prev, current: 0 }));
  };

  const handleDescargarReporte = async () => {
    setMenuReporteAnchor(null);
    setDescargando(true);
    try {
      const blob = await api.solicitudes.descargarReporte(param.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Solicitudes-${param.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("La descarga se realizó con éxito");
    } catch (e) {
      toast.error("Hubo un problema al descargar el archivo");
    } finally {
      setDescargando(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({ ...prev, current: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, pageSize: newSize, current: 0 }));
  };

  const abrirDrawer = (solicitud) => {
    setSelected(solicitud);
    setRechazando(false);
    setMotivo("");
  };

  const cerrarDrawer = () => {
    if (procesando) return;
    setSelected(null);
    setRechazando(false);
    setMotivo("");
  };

  const aprobarSolicitud = async (id) => {
    if (!id) return;
    setProcesando(true);
    try {
      const res = await api.solicitudes.aprobar(id);
      if (res.status === "success") {
        toast.success("Solicitud aprobada. Se enviaron los tickets por email.");
        setSelected(null);
        fetchSolicitudes();
      } else {
        toast.error(res.message || "No se pudo aprobar la solicitud");
      }
    } catch (e) {
      toast.error("Error al aprobar la solicitud");
    } finally {
      setProcesando(false);
    }
  };

  const aprobar = () => aprobarSolicitud(selected?.id);

  // desde el menú de acceso directo: aprobar o abrir el drawer en modo rechazo (para el motivo)
  const aprobarDesdeMenu = () => {
    const s = menuSolicitud;
    cerrarMenu();
    if (s) aprobarSolicitud(s.id);
  };
  const rechazarDesdeMenu = () => {
    const s = menuSolicitud;
    cerrarMenu();
    if (s) {
      setSelected(s);
      setMotivo("");
      setRechazando(true);
    }
  };

  const confirmarRechazo = async () => {
    if (!selected) return;
    if (!motivo.trim()) {
      toast.error("El motivo del rechazo es obligatorio");
      return;
    }
    setProcesando(true);
    try {
      const res = await api.solicitudes.rechazar(selected.id, motivo.trim());
      if (res.status === "success") {
        toast.success("Solicitud rechazada. Se notificó al solicitante.");
        setSelected(null);
        setRechazando(false);
        setMotivo("");
        fetchSolicitudes();
      } else {
        toast.error(res.message || "No se pudo rechazar la solicitud");
      }
    } catch (e) {
      toast.error("Error al rechazar la solicitud");
    } finally {
      setProcesando(false);
    }
  };

  if (firstLoad && loading) return <Loading />;

  const itemsSel = (selected?.carrito && selected.carrito.items) || [];

  const emptyMessage = {
    pendiente: "No hay solicitudes pendientes.",
    aprobada: "No hay solicitudes aprobadas.",
    rechazada: "No hay solicitudes rechazadas.",
  }[activeTab];

  return (
    <Container sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          sx={{ '& .MuiTabs-indicator': { backgroundColor: '#5656ff' }, '& .Mui-selected': { color: '#5656ff !important' } }}
        >
          {ESTADOS_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} sx={{ textTransform: 'none', fontFamily: 'Bricolage Grotesque', fontWeight: 600 }} />
          ))}
        </Tabs>

        <IconButton onClick={(e) => setMenuReporteAnchor(e.currentTarget)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={menuReporteAnchor} open={Boolean(menuReporteAnchor)} onClose={() => setMenuReporteAnchor(null)}>
          <MenuItem onClick={handleDescargarReporte}>
            <ListItemIcon>
              {descargando ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon sx={{ color: "#5656FF" }} />}
            </ListItemIcon>
            <ListItemText>{descargando ? "Descargando..." : "Descargar solicitudes"}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <div className="d-flex justify-content-between flex-wrap gap-2 mt-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch(searchValue);
          }}
          className="d-flex"
          style={{ maxWidth: "450px", width: "100%" }}
        >
          <TextField
            name="q"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Buscar por solicitante o información de la solicitud"
            type="search"
            variant="outlined"
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              endAdornment: (
                <IconButton type="submit" size="small" sx={{ marginRight: "-8px" }}>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </form>
      </div>

      <TableContainer component={Paper} className="mt-3">
        <Table aria-label="solicitudes table">
          <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
            <TableRow sx={{ borderColor: "#E6E6FF" }}>
              <TableCell sx={headCellSx}>#</TableCell>
              <TableCell sx={headCellSx}>Solicitante</TableCell>
              <TableCell sx={headCellSx}>Email</TableCell>
              <TableCell sx={headCellSx} align="center">Entradas</TableCell>
              <TableCell sx={headCellSx} align="center">Estado</TableCell>
              <TableCell sx={headCellSx} align="center">Fecha</TableCell>
              <TableCell sx={headCellSx} align="center">Acción</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : solicitudes.length > 0 ? (
              solicitudes.map((s) => {
                const usuario = s.usuario || {};
                const items = (s.carrito && s.carrito.items) || [];
                const totalEntradas = items.reduce((acc, it) => acc + (it.cantidad || 0), 0);
                const estilo = ESTADO_STYLE[s.estado] || ESTADO_STYLE.pendiente;
                const esPendiente = s.estado === "pendiente";
                return (
                  <TableRow key={s.id} hover sx={{ cursor: "pointer" }} onClick={() => abrirDrawer(s)}>
                    <TableCell>{s.id}</TableCell>
                    <TableCell>{`${usuario.nombre || ""} ${usuario.apellido || ""}`.trim() || "-"}</TableCell>
                    <TableCell>{usuario.usuario}</TableCell>
                    <TableCell align="center">{totalEntradas}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={estilo.label} sx={{ backgroundColor: estilo.bg, color: estilo.color, fontFamily: "Bricolage Grotesque" }} />
                    </TableCell>
                    <TableCell align="center">
                      {s.fecha_creacion ? moment(s.fecha_creacion).local().format("DD/MM/YYYY HH:mm") : ""}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirDrawer(s);
                            }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#5656ff" }} />
                          </IconButton>
                        </Tooltip>
                        {esPendiente && (
                          <IconButton size="small" onClick={(e) => abrirMenu(e, s)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  <Alert severity="info">{emptyMessage}</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          className="d-flex justify-content-center"
          rowsPerPageOptions={[10, 25, 50]}
          count={pagination.total || 0}
          rowsPerPage={pagination.pageSize}
          page={pagination.current}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Registros p/página"
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Menú de acceso directo (aprobar / rechazar) */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={cerrarMenu}>
        <MenuItem onClick={aprobarDesdeMenu} disabled={procesando}>
          <ListItemIcon>
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#2e7d32" }} />
          </ListItemIcon>
          <ListItemText>Aprobar y enviar</ListItemText>
        </MenuItem>
        <MenuItem onClick={rechazarDesdeMenu} disabled={procesando}>
          <ListItemIcon>
            <HighlightOffIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          </ListItemIcon>
          <ListItemText>Rechazar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Drawer lateral derecho con el detalle */}
      <Drawer anchor="right" open={!!selected} onClose={cerrarDrawer}>
        <Box sx={{ width: { xs: "100vw", sm: 480 }, display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #eee" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ color: "#0B0060", fontWeight: 600 }}>
                Solicitud #{selected?.id}
              </Typography>
              {selected && (
                <Chip
                  size="small"
                  label={(ESTADO_STYLE[selected.estado] || ESTADO_STYLE.pendiente).label}
                  sx={{
                    backgroundColor: (ESTADO_STYLE[selected.estado] || ESTADO_STYLE.pendiente).bg,
                    color: (ESTADO_STYLE[selected.estado] || ESTADO_STYLE.pendiente).color,
                    fontFamily: "Bricolage Grotesque",
                  }}
                />
              )}
            </Box>
            <IconButton onClick={cerrarDrawer} disabled={procesando}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
            {selected && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#0B0060", mb: 2 }}>
                  Datos del solicitante
                </Typography>
                <Campo label="Nombre" value={`${selected.usuario?.nombre || ""} ${selected.usuario?.apellido || ""}`.trim()} />
                <Campo label="Email" value={selected.usuario?.usuario} />
                <Campo
                  label="Fecha de solicitud"
                  value={selected.fecha_creacion ? moment(selected.fecha_creacion).local().format("DD/MM/YYYY HH:mm") : "-"}
                />

                <Divider sx={{ my: 2 }} />



                {itemsSel.map((item, i) => {
                  const grupos = agruparRespuestas(item);
                  const variosTickets = grupos.length > 1;
                  return (
                    <Box key={i} sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#0B0060", mb: 2 }}>
                          Entradas solicitadas
                        </Typography>
                        <Chip
                          label={`${item.cantidad} x ${item.tarifa ? item.tarifa.concepto : "Tarifa"}`}
                          size="small"
                          sx={{ backgroundColor: "#E6E6FF", color: "#0B0060", fontWeight: 600, mb: 1.5 }}
                        />
                      </Box>
                      {grupos.length === 0 ? (
                        <Typography variant="body2" sx={{ color: "#999" }}>Sin información adicional</Typography>
                      ) : (
                        grupos.map((grupo, gi) => (
                          <Box
                            key={gi}
                            sx={{
                              mb: 0.5,
                              p: variosTickets ? 2 : 0,
                              borderRadius: variosTickets ? "10px" : 0,
                              border: variosTickets ? "1px solid #ECECF5" : "none",
                              backgroundColor: variosTickets ? "#FAFAFF" : "transparent",
                            }}
                          >
                            {variosTickets && (
                              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#5656ff", mb: 1 }}>
                                Ticket {gi + 1}
                              </Typography>
                            )}
                            {grupo.map((r, j) => (
                              <Campo
                                key={j}
                                label={(r.informacion_adicional && r.informacion_adicional.informacion_adicional) || "Respuesta"}
                                value={r.respuesta}
                              />
                            ))}
                          </Box>
                        ))
                      )}
                    </Box>
                  );
                })}

                {rechazando && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#8A1F1F", mb: 1 }}>
                      Motivo del rechazo
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      autoFocus
                      placeholder="Se le enviará al solicitante por email"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                  </>
                )}

                {selected.estado !== "pendiente" && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#0B0060", mb: 2 }}>
                      Resolución
                    </Typography>
                    <Campo
                      label={selected.estado === "aprobada" ? "Fecha de aprobación" : "Fecha de rechazo"}
                      value={
                        selected.estado === "aprobada"
                          ? (selected.fecha_confirmacion ? moment(selected.fecha_confirmacion).local().format("DD/MM/YYYY HH:mm") : "-")
                          : (selected.fecha_rechazo ? moment(selected.fecha_rechazo).local().format("DD/MM/YYYY HH:mm") : "-")
                      }
                    />
                    <Campo
                      label="Resuelto por"
                      value={selected.resolvedor ? `${selected.resolvedor.nombre || ""} ${selected.resolvedor.apellido || ""}`.trim() : "-"}
                    />
                    {selected.estado === "rechazada" && (
                      <Campo label="Motivo del rechazo" value={selected.observaciones} />
                    )}
                  </>
                )}
              </>
            )}
          </Box>

          {selected?.estado === "pendiente" && (
            <Box sx={{ p: 2, borderTop: "1px solid #eee", display: "flex", gap: 1 }}>
              {!rechazando ? (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<HighlightOffIcon />}
                    disabled={procesando}
                    onClick={() => setRechazando(true)}
                  >
                    Rechazar
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                    disabled={procesando}
                    onClick={aprobar}
                  >
                    Aprobar y enviar
                  </Button>
                </>
              ) : (
                <>
                  <Button fullWidth variant="text" disabled={procesando} onClick={() => setRechazando(false)}>
                    Cancelar
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    disabled={procesando}
                    startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={confirmarRechazo}
                  >
                    Rechazar y notificar
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </Container>
  );
};

export default withAuthenticationRequired(SolicitudesTickets, {
  onRedirecting: () => <Loading />,
});
